// Procedural pedestrians: faces, bodies, outfits and dogs.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, P, add, canvasTex } from '../lib/factory.js';
import { denimTex, noiseFill } from '../textures/cloth.js';

export let walkers, PEOPLE, limbGeo, makePerson, makeDog;

export function buildPeople() {
  // tapered limb with rounded ends, hanging from y = 0 down to y = -len
  limbGeo = function limbGeo(len, r1, r2) {
    return gcache(`limb${len}|${r1}|${r2}`, () => {
      const pts = [];
      for (let i = 0; i <= 4; i++) {
        const a = ((i / 4) * PI) / 2;
        pts.push(new THREE.Vector2(r2 * Math.sin(a), -len + r2 * (1 - Math.cos(a))));
      }
      for (let i = 0; i <= 4; i++) {
        const a = PI / 2 - ((i / 4) * PI) / 2;
        pts.push(new THREE.Vector2(r1 * Math.sin(a), -r1 + r1 * Math.cos(a)));
      }
      return new THREE.LatheGeometry(pts, 14);
    });
  };
  makePerson = function makePerson(seated) {
    const sc = pick(PEOPLE.shirts),
      pat = Math.floor(rnd() * 3),
      si = Math.floor(rnd() * PEOPLE.skins.length),
      lc = pick(PEOPLE.legs),
      shc = pick([0x1d1d1d, 0xf2f0ea, 0x6b4a33]);
    const hc = pick(PEOPLE.hairs),
      skirt = !seated && rnd() < 0.22,
      shortSleeve = rnd() < 0.5,
      shorts = !skirt && rnd() < 0.2;
    const g = new THREE.Group(),
      legs = [],
      shirt = pmat('s' + sc + pat, () =>
        M(sc, { map: shirtTex[pat], flatShading: false, roughness: 0.85 }),
      );
    const skin = pmat('k' + si, () => M(PEOPLE.skins[si], { flatShading: false, roughness: 0.6 })),
      face = pmat('f' + si, () =>
        M(0xffffff, { map: faceTex[si], flatShading: false, roughness: 0.6 }),
      );
    const lm = pmat('l' + lc, () =>
      lc === 0x2f3c55 || lc === 0x1f2a40
        ? M(0xffffff, { map: denimTex, flatShading: false, roughness: 0.9 })
        : M(lc, { map: shirtTex[0], flatShading: false, roughness: 0.85 }),
    );
    const shoe = pmat('h' + shc, () => M(shc, { flatShading: false, roughness: 0.5 })),
      hair = pmat('r' + hc, () => M(hc, { map: hairTex, flatShading: false, roughness: 0.7 }));
    add(torsoGeo, shirt, 0, 0, 0, g);
    add(
      gcache('neck', () => new THREE.CylinderGeometry(0.042, 0.05, 0.1, 12)),
      skin,
      0,
      1.36,
      0,
      g,
    );
    add(headGeo, face, 0, 1.39, 0.005, g);
    for (const sx of [-1, 1])
      add(
        gcache('ear', () => new THREE.SphereGeometry(0.024, 10, 8).scale(0.5, 1, 0.8)),
        skin,
        sx * 0.098,
        1.51,
        0,
        g,
      );
    const long = rnd() < 0.4;
    add(hairCapGeo, hair, 0, 1.39, -0.006, g);
    if (long)
      add(
        gcache('hairBack', () =>
          new THREE.CylinderGeometry(0.105, 0.118, 0.3, 20, 1, true, PI / 2, PI).scale(1, 1, 1.05),
        ),
        hair,
        0,
        1.43,
        -0.01,
        g,
      ).material.side = THREE.DoubleSide;
    if (skirt)
      add(
        skirtGeo,
        pmat('sk' + lc, () =>
          M(pick([0x2f3c55, 0x8e5bb5, 0x1d1d1d, 0xc2457a]), {
            flatShading: false,
            side: THREE.DoubleSide,
            roughness: 0.85,
          }),
        ),
        0,
        0.5,
        0,
        g,
      );
    for (const sx of [-1, 1]) {
      const hip = new THREE.Group();
      hip.position.set(sx * 0.085, 0.66, 0);
      g.add(hip);
      add(limbGeo(0.38, 0.072, 0.055), skirt || shorts ? skin : lm, 0, 0, 0, hip);
      if (shorts) add(limbGeo(0.2, 0.078, 0.07), lm, 0, 0, 0, hip);
      const knee = new THREE.Group();
      knee.position.y = -0.36;
      hip.add(knee);
      add(limbGeo(0.32, 0.052, 0.038), skirt || shorts ? skin : lm, 0, 0, 0, knee);
      add(
        gcache('shoe', () => new RoundedBoxGeometry(0.085, 0.06, 0.2, 2, 0.025)),
        shoe,
        0,
        -0.33,
        0.045,
        knee,
      );
      hip.userData.j = knee;
      legs.push(hip);
      if (seated) {
        hip.rotation.x = -PI / 2;
        knee.rotation.x = PI / 2;
      }
    }
    for (const sx of [-1, 1]) {
      const sh = new THREE.Group();
      sh.position.set(sx * 0.19, 1.22, 0);
      sh.rotation.z = sx * 0.08;
      g.add(sh);
      add(limbGeo(0.27, 0.054, 0.044), shirt, 0, 0, 0, sh);
      const el = new THREE.Group();
      el.position.y = -0.26;
      el.rotation.x = -0.18;
      sh.add(el);
      sh.userData.j = el;
      add(limbGeo(0.24, 0.042, 0.033), shortSleeve ? skin : shirt, 0, 0, 0, el);
      add(
        gcache('hand', () => new RoundedBoxGeometry(0.05, 0.085, 0.03, 2, 0.012)),
        skin,
        0,
        -0.27,
        0,
        el,
      );
      legs.push(sh);
    }
    if (!seated && rnd() < 0.3)
      add(
        gcache('bag', () => new RoundedBoxGeometry(0.07, 0.26, 0.24, 2, 0.03)),
        pmat('b' + pat, () =>
          M(pick([0x6b4a33, 0x1d2433, 0xd9534f, 0xe8d8b8]), { flatShading: false }),
        ),
        0.25,
        0.8,
        0,
        g,
      );
    // accessories: belt, collar, cap, sunglasses, beard, backpack, phone, ponytail
    add(
      gcache('belt', () =>
        new THREE.CylinderGeometry(0.158, 0.158, 0.035, 20).scale(1.12, 1, 0.68),
      ),
      pmat('belt', () => M(0x3a2a1e, { flatShading: false })),
      0,
      0.78,
      0,
      g,
    );
    add(
      gcache('collar', () => new THREE.TorusGeometry(0.055, 0.018, 6, 16).rotateX(PI / 2)),
      shirt,
      0,
      1.33,
      0,
      g,
    );
    if (rnd() < 0.18) {
      const capM = pmat('cap' + pat, () =>
        M(pick([0x1d2433, 0xd9412c, 0xf2f0ea, 0x2e9e6a]), { flatShading: false }),
      );
      add(
        gcache('cap', () => new THREE.SphereGeometry(0.118, 18, 8, 0, PI * 2, 0, PI * 0.5)),
        capM,
        0,
        1.575,
        -0.005,
        g,
      );
      add(
        gcache('visor', () =>
          new THREE.CylinderGeometry(0.09, 0.09, 0.012, 16, 1, false, -PI / 2, PI).scale(1, 1, 1.2),
        ),
        capM,
        0,
        1.585,
        0.07,
        g,
      );
    }
    if (rnd() < 0.18) {
      const gl = pmat('glasses', () => P(0x111214, { roughness: 0.1, metalness: 0.6 }));
      for (const sx of [-1, 1])
        add(
          gcache('lens', () => new THREE.CylinderGeometry(0.024, 0.024, 0.008, 12).rotateX(PI / 2)),
          gl,
          sx * 0.035,
          1.555,
          0.1,
          g,
        );
      add(
        gcache('bridge', () => new THREE.BoxGeometry(0.1, 0.006, 0.006)),
        gl,
        0,
        1.56,
        0.102,
        g,
      );
    }
    if (!long && rnd() < 0.2)
      add(
        gcache('beard', () =>
          new THREE.SphereGeometry(0.1, 16, 8, 0, PI, PI * 0.5, PI * 0.3)
            .rotateY(-PI / 2)
            .scale(0.95, 1.1, 1.05),
        ),
        hair,
        0,
        1.49,
        0.008,
        g,
      );
    if (long && rnd() < 0.5)
      add(
        gcache('pony', () => new THREE.CapsuleGeometry(0.03, 0.14, 4, 8)),
        hair,
        0,
        1.46,
        -0.12,
        g,
      ).rotation.x = 0.5;
    if (!seated && rnd() < 0.15)
      add(
        gcache('pack', () => new RoundedBoxGeometry(0.24, 0.3, 0.12, 2, 0.04)),
        pmat('pk' + pat, () =>
          M(pick([0x1d2433, 0x8a5a3c, 0x2f6fd0, 0x3a3f47]), { flatShading: false }),
        ),
        0,
        1.02,
        -0.15,
        g,
      );
    if (!seated && rnd() < 0.2)
      add(
        gcache('phone', () => new RoundedBoxGeometry(0.04, 0.075, 0.008, 1, 0.004)),
        pmat('phone', () => P(0x15161a, { roughness: 0.2 })),
        0,
        -0.3,
        0.03,
        legs[3].userData.j,
      );
    // same real-world height as the character models (~0.92 units)
    g.scale.setScalar(rr(0.52, 0.6));
    return { g, legs };
  };
  makeDog = function makeDog() {
    const g = new THREE.Group(),
      fur = M(pick([0x8a6a4a, 0x2b2b2b, 0xe8dcc4, 0xc9a66b]), { flatShading: false }),
      legs = [];
    add(new THREE.CapsuleGeometry(0.09, 0.32, 4, 10).rotateX(PI / 2), fur, 0, 0.3, 0, g);
    add(new THREE.SphereGeometry(0.085, 12, 10), fur, 0, 0.4, 0.25, g);
    add(new THREE.CapsuleGeometry(0.04, 0.06, 4, 8).rotateX(PI / 2), fur, 0, 0.38, 0.34, g);
    add(new THREE.CapsuleGeometry(0.02, 0.14, 4, 6), fur, 0, 0.38, -0.24, g).rotation.x = -0.8;
    for (const [x, z] of [
      [-0.06, 0.14],
      [0.06, 0.14],
      [-0.06, -0.14],
      [0.06, -0.14],
    ]) {
      const l = new THREE.Group();
      l.position.set(x, 0.24, z);
      g.add(l);
      add(new THREE.CylinderGeometry(0.022, 0.022, 0.22, 6), fur, 0, -0.11, 0, l);
      legs.push(l);
    }
    scene.add(g);
    return { g, legs };
  };

  // pedestrians strolling around the blocks on the sidewalk
  walkers = [];
  PEOPLE = {
    shirts: [
      0xd9534f, 0x3b7dd8, 0xf0c419, 0x2e9e6a, 0xf2f0ea, 0x8e5bb5, 0xe67e22, 0x34495e, 0xc2457a,
      0x7a8f5a,
    ],
    legs: [0x2f3c55, 0x3a3a3a, 0x6b5a45, 0x1f2a40, 0xc9b99a],
    hairs: [0x2b1d14, 0x4a3020, 0x111111, 0xc9a66b, 0x8a8a8a, 0x7a3a1a],
    skins: [0xf1c8a5, 0xe0b08a, 0xb98563, 0x8d5a3b],
  };
  const personMats = new Map();
  const pmat = (key, make) => {
    if (!personMats.has(key)) personMats.set(key, make());
    return personMats.get(key);
  };
  const shirtTex = [
    canvasTex(64, 64, (c) => {
      c.fillStyle = '#fff';
      c.fillRect(0, 0, 64, 64);
      noiseFill(c, 64, 64, 500, ['rgba(0,0,0,.06)', 'rgba(255,255,255,.4)'], 1);
    }),
    canvasTex(64, 64, (c) => {
      c.fillStyle = '#fff';
      c.fillRect(0, 0, 64, 64);
      c.fillStyle = 'rgba(20,24,40,.55)';
      for (let y = 0; y < 64; y += 8) c.fillRect(0, y, 64, 3);
    }),
    canvasTex(64, 64, (c) => {
      c.fillStyle = '#fff';
      c.fillRect(0, 0, 64, 64);
      c.fillStyle = 'rgba(0,0,0,.28)';
      for (let i = 0; i < 64; i += 16) {
        c.fillRect(i, 0, 5, 64);
        c.fillRect(0, i, 64, 5);
      }
      c.fillStyle = 'rgba(0,0,0,.12)';
      for (let i = 8; i < 64; i += 16) {
        c.fillRect(i, 0, 2, 64);
        c.fillRect(0, i, 64, 2);
      }
    }),
  ];
  // face drawn for the lathe head: front of the head sits at u = .5, height maps linearly to v
  // painted, storybook-style faces (no sculpted features): big soft eyes with a highlight, light brows, small mouth
  const faceTex = PEOPLE.skins.map((sk) =>
    canvasTex(256, 128, (c) => {
      const base = '#' + sk.toString(16).padStart(6, '0');
      c.fillStyle = base;
      c.fillRect(0, 0, 256, 128);
      c.fillStyle = 'rgba(230,120,110,.22)';
      for (const ex of [106, 150]) {
        c.beginPath();
        c.ellipse(ex, 74, 9, 5, 0, 0, PI * 2);
        c.fill();
      }
      for (const ex of [116, 140]) {
        c.fillStyle = '#2b1d14';
        c.beginPath();
        c.ellipse(ex, 57, 4.2, 6, 0, 0, PI * 2);
        c.fill();
        c.fillStyle = '#ffffff';
        c.beginPath();
        c.arc(ex + 1.4, 54.5, 1.6, 0, PI * 2);
        c.fill();
        c.strokeStyle = 'rgba(40,25,15,.55)';
        c.lineWidth = 2;
        c.lineCap = 'round';
        c.beginPath();
        c.moveTo(ex - 6, 46);
        c.quadraticCurveTo(ex, 43, ex + 6, 46);
        c.stroke();
      }
      c.strokeStyle = 'rgba(0,0,0,.12)';
      c.lineWidth = 1.5;
      c.beginPath();
      c.moveTo(128, 64);
      c.lineTo(127, 75);
      c.stroke();
      c.strokeStyle = 'rgba(120,50,45,.75)';
      c.lineWidth = 2;
      c.beginPath();
      c.moveTo(122, 88);
      c.quadraticCurveTo(128, 92, 134, 88);
      c.stroke();
    }),
  );
  const geoCache = new Map(),
    gcache = (k, make) => {
      if (!geoCache.has(k)) geoCache.set(k, make());
      return geoCache.get(k);
    };
  const torsoGeo = gcache('torso', () => {
    // hips, waist, chest and shoulders; flattened front to back
    const prof = [
      [0.001, 0.6],
      [0.12, 0.61],
      [0.155, 0.68],
      [0.158, 0.76],
      [0.142, 0.86],
      [0.135, 0.93],
      [0.15, 1.02],
      [0.168, 1.12],
      [0.175, 1.2],
      [0.16, 1.26],
      [0.1, 1.31],
      [0.05, 1.34],
      [0.001, 1.345],
    ];
    return new THREE.LatheGeometry(
      prof.map(([r, y]) => new THREE.Vector2(r, y)),
      20,
    )
      .rotateY(PI)
      .scale(1.12, 1, 0.68);
  });
  const headGeo = gcache('head', () => {
    const pts = [];
    for (let i = 0; i <= 14; i++) {
      const t = i / 14;
      pts.push(
        new THREE.Vector2(
          Math.max(
            0.001,
            0.106 *
              Math.pow(Math.sin(PI * Math.min(t, 0.999)), 0.55) *
              (t < 0.35 ? 0.8 + (0.2 * t) / 0.35 : 1),
          ),
          t * 0.26,
        ),
      );
    }
    return new THREE.LatheGeometry(pts, 24).rotateY(PI).scale(0.92, 1, 1.06);
  });
  // hairline sits above the brows at the front and lower at the back and sides
  const hairCapGeo = gcache('hair', () => {
    const prof = (t0) => {
      const pts = [];
      for (let i = 0; i <= 10; i++) {
        const t = t0 + (i / 10) * (1 - t0);
        pts.push(
          new THREE.Vector2(
            Math.max(0.001, 0.114 * Math.pow(Math.sin(PI * Math.min(t, 0.999)), 0.55)),
            t * 0.275 - 0.004,
          ),
        );
      }
      return pts;
    };
    const front = new THREE.LatheGeometry(prof(0.66), 24, -PI / 2, PI),
      back = new THREE.LatheGeometry(prof(0.4), 24, PI / 2, PI);
    return mergeGeometries([front, back]).scale(0.95, 1, 1.1);
  });
  const skirtGeo = gcache('skirt', () =>
    new THREE.CylinderGeometry(0.15, 0.22, 0.36, 20, 1, true).scale(1, 1, 0.8),
  );
  const hairTex = canvasTex(64, 64, (c) => {
    c.fillStyle = '#fff';
    c.fillRect(0, 0, 64, 64);
    for (let k = 0; k < 220; k++) {
      c.strokeStyle = `rgba(0,0,0,${rr(0.1, 0.35)})`;
      c.lineWidth = 1;
      const x = rnd() * 64;
      c.beginPath();
      c.moveTo(x, 0);
      c.quadraticCurveTo(x + rr(-6, 6), 32, x + rr(-4, 4), 64);
      c.stroke();
    }
  });
}
